import { featureCollection, type Point, point } from '@turf/helpers';
import type { MapMouseEvent } from 'mapbox-gl';
import { useCallback, useMemo, useState } from 'react';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import type { WaypointCategoryUid } from '@alltrails/types/Waypoint';
import { useGetUserWaypointsQuery } from '@alltrails/waypoints/redux/waypointsApi';
import { DEFAULT_WAYPOINT_CATEGORY_UID } from '@alltrails/waypoints/utils/waypointCategories';
import { useAppDispatch, useAppSelector } from '../../redux';
import { selectCustomRouteWaypointServerIds } from '../../redux/customRoute';
import { clearSelectedWaypoint, setSelectedWaypoint, setUserWaypointDraft, updateCursor } from '../../redux/reducer';
import { userWaypointsId } from '../../utils/layers';
import WaypointPinLayer from './WaypointPinLayer';

const SOURCE_ID = 'user-waypoints-source';

type FeatureProperties = {
  id: number;
  title?: string;
  categoryUid: string;
};

const UserWaypoints = () => {
  const dispatch = useAppDispatch();
  const { data: waypoints } = useGetUserWaypointsQuery({ globalOnly: true });
  const isEditingCustomRouteWaypoint = useAppSelector(state => state.customRoute?.isEditingWaypoint);
  const customRouteServerIds = useAppSelector(selectCustomRouteWaypointServerIds);
  // Live-preview overlay for an in-progress global waypoint edit. Set by the user-waypoint sidebar.
  const userWaypointDraft = useAppSelector(state => state.map.userWaypointDraft);
  // The waypoint currently being viewed in the sidebar — kept enlarged on the map regardless of hover.
  const selectedUserWaypointId = useAppSelector(state => (state.map.selectedWaypoint?.source === 'user' ? state.map.selectedWaypoint.id : undefined));

  const [activeWaypointId, setActiveWaypointId] = useState<number>();

  const visibleWaypoints = useMemo(() => {
    if (!waypoints?.length) {
      return undefined;
    }
    // De-dupe with the currently-loaded custom route's waypoints.
    // Custom route waypoints should take precedence over their global counterparts.
    const serverIdSet = new Set(customRouteServerIds.filter((id): id is number => id !== undefined));
    if (serverIdSet.size === 0) {
      return waypoints;
    }
    return waypoints.filter(w => !serverIdSet.has(w.id));
  }, [waypoints, customRouteServerIds]);

  // The waypoint currently being edited — split out so it can be rendered as a draggable Marker rather
  // than a static source feature.
  const editingWaypoint = useMemo(() => {
    const id = userWaypointDraft?.id;
    if (id === undefined) {
      return undefined;
    }
    return visibleWaypoints?.find(w => w.id === id);
  }, [visibleWaypoints, userWaypointDraft?.id]);

  const featureCollectionData = useMemo(() => {
    if (!visibleWaypoints?.length) {
      return featureCollection<Point>([]);
    }

    return featureCollection<Point>(
      visibleWaypoints
        // The edited waypoint is rendered separately as a draggable Marker.
        .filter(waypoint => waypoint.id !== editingWaypoint?.id)
        .map(waypoint => {
          const { id, location } = waypoint;
          const { latitude, longitude } = location;
          // If the user is mid-edit on this waypoint, the draft's name/showTitle/category override the server values for live preview
          const isDraftMatch = userWaypointDraft?.id === id;
          const name = isDraftMatch && userWaypointDraft.name !== undefined ? userWaypointDraft.name : waypoint.name;
          const showTitle =
            isDraftMatch && userWaypointDraft.showTitle !== undefined
              ? userWaypointDraft.showTitle
              : (waypoint.waypointDisplayProperty?.showTitle ?? false);
          const waypointCategory =
            isDraftMatch && userWaypointDraft.waypointCategory !== undefined ? userWaypointDraft.waypointCategory : waypoint.waypointCategory;
          const categoryUid = waypointCategory?.uid ?? DEFAULT_WAYPOINT_CATEGORY_UID;
          const title = showTitle ? name : undefined;
          // No `activeOrSelected` here — WaypointPinLayer derives that from `activeWaypointId` via a Mapbox expression.
          return point([longitude, latitude], { id, title, categoryUid });
        })
    );
  }, [visibleWaypoints, editingWaypoint?.id, userWaypointDraft]);

  const selectedWaypointDescriptor = useMemo(() => {
    if (!editingWaypoint) {
      return undefined;
    }
    const isDraftMatch = userWaypointDraft?.id === editingWaypoint.id;
    const lat = isDraftMatch && userWaypointDraft.latitude !== undefined ? userWaypointDraft.latitude : editingWaypoint.location.latitude;
    const lng = isDraftMatch && userWaypointDraft.longitude !== undefined ? userWaypointDraft.longitude : editingWaypoint.location.longitude;
    const name = isDraftMatch && userWaypointDraft.name !== undefined ? userWaypointDraft.name : editingWaypoint.name;
    const showTitle =
      isDraftMatch && userWaypointDraft.showTitle !== undefined
        ? userWaypointDraft.showTitle
        : (editingWaypoint.waypointDisplayProperty?.showTitle ?? false);
    const waypointCategory =
      isDraftMatch && userWaypointDraft.waypointCategory !== undefined ? userWaypointDraft.waypointCategory : editingWaypoint.waypointCategory;
    return {
      id: editingWaypoint.id,
      latitude: lat,
      longitude: lng,
      title: showTitle ? name : undefined,
      categoryUid: waypointCategory?.uid as WaypointCategoryUid | undefined
    };
  }, [editingWaypoint, userWaypointDraft]);

  const onSelectedDragEnd = useCallback(
    (event: { lngLat: { lng: number; lat: number } }) => {
      const editingId = userWaypointDraft?.id;
      if (editingId === undefined) {
        return;
      }
      dispatch(
        setUserWaypointDraft({
          id: editingId,
          latitude: Number(event.lngLat.lat.toFixed(6)),
          longitude: Number(event.lngLat.lng.toFixed(6))
        })
      );
    },
    [dispatch, userWaypointDraft?.id]
  );

  const onHover = useCallback(
    (properties: FeatureProperties) => {
      setActiveWaypointId(properties.id);
      dispatch(updateCursor('pointer'));
    },
    [dispatch]
  );

  const onHoverEnd = useCallback(() => {
    setActiveWaypointId(undefined);
    dispatch(updateCursor('unset'));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const properties = event.features[0].properties as FeatureProperties;
        onMouseEnter(properties);
      }
    },
    [onMouseEnter]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (!event.features) {
        return;
      }
      // Block clicks when an in-progress route waypoint edit is open, matching the gate that
      // CustomRouteWaypoints already enforces — the user shouldn't be able to lose unsaved edits.
      if (isEditingCustomRouteWaypoint) {
        return;
      }
      event.preventDefault(); // Allows other layers to detect that this click was handled.
      const id = event.features[0]?.properties?.id;
      if (typeof id === 'number') {
        if (selectedUserWaypointId === id) {
          dispatch(clearSelectedWaypoint());
        } else {
          dispatch(setSelectedWaypoint({ source: 'user', id }));
        }
      }
    },
    [dispatch, isEditingCustomRouteWaypoint, selectedUserWaypointId]
  );

  // Hover wins over selection for the enlarged-pin highlight (matches prior behavior).
  const composedActiveWaypointId = activeWaypointId ?? selectedUserWaypointId;

  return (
    <WaypointPinLayer
      activeWaypointId={composedActiveWaypointId}
      isDraggable={selectedWaypointDescriptor !== undefined}
      layerId={userWaypointsId}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onSelectedDragEnd={onSelectedDragEnd}
      source={{ type: 'geojson', data: featureCollectionData }}
      sourceId={SOURCE_ID}
      variant="user"
      waypointMarkerProperties={selectedWaypointDescriptor}
    />
  );
};

export default UserWaypoints;
